import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { openRazorpayCheckout } from "../lib/deviceBridge";
import { getAppConfig } from "../lib/appConfig";

// All data-loading + actions for the merchant dashboard.
export function useMerchantData() {
  const [tab, setTab] = useState("issue");
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [form, setForm] = useState({ customer_mobile: "", token_count: "" });
  const [busy, setBusy] = useState(false);
  const [needProfile, setNeedProfile] = useState(false);
  const [biz, setBiz] = useState({ business_name: "", gst: "", pan: "", bank_account: "" });
  const [kyc, setKyc] = useState(null);
  const [refCode, setRefCode] = useState((localStorage.getItem("paras_referral_code") || ""));
  const [refApplied, setRefApplied] = useState(null);
  const [appCfg, setAppCfg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/merchant/me");
      setMe(data);
    } catch (e) {
      if (e?.response?.status === 404) setNeedProfile(true);
    }
    api.get("/public/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    api.get("/merchant/tokens").then((r) => setTokens(r.data.tokens)).catch(() => {});
    api.get("/merchant/kyc").then((r) => setKyc(r.data)).catch(() => {});
    getAppConfig().then(setAppCfg).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (localStorage.getItem("paras_referral_code")) setTab("subscribe");
  }, []);

  const createProfile = async () => {
    if (!biz.business_name) return toast.error("Business name required");
    setBusy(true);
    try {
      await api.post("/auth/become-merchant", biz);
      toast.success("Merchant profile created");
      setNeedProfile(false);
      await load();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const applyReferral = async () => {
    const code = refCode.trim().toUpperCase();
    if (!code) return toast.error("Enter a referral code");
    setBusy(true);
    try {
      const { data } = await api.post("/merchant/referral/apply", { referral_code: code });
      setRefApplied(data);
      if (data.status === "INVALID")
        toast.warning("Referral code recorded but flagged — no reward will apply");
      else toast.success("Referral code applied");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid referral code");
    } finally { setBusy(false); }
  };

  const applyRefIfNeeded = async () => {
    const code = refCode.trim().toUpperCase();
    if (code && !refApplied) {
      try {
        const { data } = await api.post("/merchant/referral/apply", { referral_code: code });
        setRefApplied(data);
      } catch (e) { toast.warning(e?.response?.data?.detail || "Referral code ignored"); }
    }
  };

  // LIVE payment: real Razorpay checkout (native UPI in the app) -> server verifies signature.
  const subscribe = async (plan_id) => {
    setBusy(true);
    try {
      await applyRefIfNeeded();
      const { data } = await api.post("/merchant/subscribe/order", { plan_id });
      const order = data.order;
      const resp = await openRazorpayCheckout({
        key: data.key_id || appCfg?.payments?.razorpay_key_id,
        order_id: order.id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Paras CashBack",
        description: "Merchant subscription",
        prefill: { email: me?.email || "", contact: me?.phone || me?.mobile || "" },
      });
      await api.post("/payments/verify", {
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
      });
      localStorage.removeItem("paras_referral_code");
      toast.success("Subscription activated. Tokens allocated.");
      await load();
      setTab("issue");
    } catch (e) {
      if (e?.message === "Checkout dismissed") { setBusy(false); return; }
      toast.error(e?.response?.data?.detail || e?.message || "Payment failed");
    } finally { setBusy(false); }
  };

  // TEST-mode activation (only when backend dev payments are enabled) — no real money.
  const devActivate = async (plan_id) => {
    setBusy(true);
    try {
      await applyRefIfNeeded();
      const { data } = await api.post("/merchant/subscribe/order", { plan_id });
      await api.post("/payments/dev/confirm", { order_id: data.order.id });
      localStorage.removeItem("paras_referral_code");
      toast.success("Subscription activated (test mode). Tokens allocated.");
      await load();
      setTab("issue");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const issue = async () => {
    setBusy(true);
    try {
      const idem = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
      const { data } = await api.post("/merchant/issue-token", {
        customer_mobile: form.customer_mobile,
        token_count: Number(form.token_count),
        idempotency_key: idem,
      });
      toast.success(`Issued ${data.issued} scratch tokens to ${form.customer_mobile}`);
      setForm({ customer_mobile: "", token_count: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const uploadKyc = async (doc_type, fileObj) => {
    if (fileObj.size > 5 * 1024 * 1024) return toast.error("File exceeds 5MB");
    const fd = new FormData();
    fd.append("doc_type", doc_type);
    fd.append("file", fileObj);
    try {
      await api.post("/merchant/kyc/upload", fd);
      toast.success("Document uploaded — pending review");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  return {
    tab, setTab, me, plans, tokens, form, setForm, busy, needProfile,
    biz, setBiz, kyc, refCode, setRefCode, refApplied, appCfg,
    createProfile, applyReferral, subscribe, devActivate, issue, uploadKyc,
  };
}
